import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface NavProps {
  onContactClick: () => void;
}

const Nav: React.FC<NavProps> = ({ onContactClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 100);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && menuOpen) setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  // Lock body scroll while mobile menu is open
  useEffect(() => {
    if (!menuOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, [menuOpen]);

  const close = () => setMenuOpen(false);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-200 ${
          scrolled || menuOpen ? 'bg-void-black border-b border-grid-line' : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" aria-label="Parsant home" onClick={close}>
            <img src="/parsant-mark-white.svg" alt="Parsant" className="h-6" />
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-8 font-mono text-sm uppercase tracking-[0.15em] text-steel">
            <Link to="/why" className="hover:text-signal-white transition-colors">Why</Link>
            <Link to="/how-we-work" className="hover:text-signal-white transition-colors">How we work</Link>
            <Link to="/services" className="hover:text-signal-white transition-colors">Services</Link>
            <Link to="/faq" className="hover:text-signal-white transition-colors">FAQ</Link>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/quiz"
              className="hidden sm:inline-block font-mono text-xs uppercase tracking-[0.15em] text-steel hover:text-command-amber transition-colors px-3 py-2"
            >
              Start AI Readiness Assessment
            </Link>
            <button
              onClick={onContactClick}
              className="btn btn-primary btn-sm"
            >
              Start a Conversation
            </button>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMenuOpen(o => !o)}
              aria-expanded={menuOpen}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-controls="mobile-nav"
              className="md:hidden w-10 h-10 flex flex-col justify-center items-center gap-1.5 text-signal-white"
            >
              <span
                className={`block w-6 h-0.5 bg-current transition-transform duration-200 ${
                  menuOpen ? 'translate-y-2 rotate-45' : ''
                }`}
              />
              <span
                className={`block w-6 h-0.5 bg-current transition-opacity duration-200 ${
                  menuOpen ? 'opacity-0' : ''
                }`}
              />
              <span
                className={`block w-6 h-0.5 bg-current transition-transform duration-200 ${
                  menuOpen ? '-translate-y-2 -rotate-45' : ''
                }`}
              />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu overlay, full-screen below the nav bar */}
      {menuOpen && (
        <div
          id="mobile-nav"
          className="md:hidden fixed inset-0 top-16 z-40 bg-void-black overflow-y-auto"
        >
          <nav className="flex flex-col px-6 py-8 gap-6 font-mono text-lg uppercase tracking-[0.15em]">
            <Link to="/why" onClick={close} className="text-steel hover:text-signal-white transition-colors">Why</Link>
            <Link to="/how-we-work" onClick={close} className="text-steel hover:text-signal-white transition-colors">How we work</Link>
            <Link to="/services" onClick={close} className="text-steel hover:text-signal-white transition-colors">Services</Link>
            <Link to="/faq" onClick={close} className="text-steel hover:text-signal-white transition-colors">FAQ</Link>
            <Link
              to="/quiz"
              onClick={close}
              className="pt-6 mt-2 border-t border-grid-line text-command-amber hover:opacity-80 transition-opacity"
            >
              Start AI Readiness Assessment →
            </Link>
          </nav>
        </div>
      )}
    </>
  );
};

export default Nav;
